<template>
  <div class="flex space-x-4">
    <div>
      <div
        class="mt-4 flex h-8 w-8 items-center justify-center rounded-full border-2 font-bold transition-colors"
        :class="
          isExpanded
            ? 'border-primary-light bg-primary text-white'
            : 'border-gray-200 bg-gray-100'
        "
      >
        {{ stepNumber }}
      </div>
      <div
        v-if="!isLastStep"
        class="relative top-1.5 mx-auto h-[calc(100%-25px)] w-0.5 bg-gray-100"
      ></div>
    </div>
    <div class="w-full min-w-0">
      <div
        class="flex items-center justify-between rounded-md border border-gray-200 p-4"
        :class="isExpanded ? 'rounded-b-none' : 'rounded-b-md'"
      >
        <div
          class="drag-handle flex min-w-0 cursor-grab items-center space-x-4 text-headline"
          :class="{ 'font-semibold': !!step.name }"
        >
          <icon-base
            :icon="IconDragHandle"
            class="shrink-0"
            width="6"
            height="10"
            viewBox="0 0 12 20"
          />
          <span class="truncate">
            {{ step.name || t("CHECKOUT.PROVIDE_STEP_DETAILS") }}
          </span>
        </div>
        <div class="flex items-center space-x-4">
          <button class="cursor-pointer" @click="removeStep">
            <icon-base
              :icon="IconTrashCanOutline"
              data-cy="step-delete-button"
            />
          </button>
          <button
            class="cursor-pointer"
            data-cy="step-expand-button"
            @click="expand"
          >
            <icon-chevron-down
              class="transform transition-transform"
              :class="{ 'rotate-180': !isExpanded }"
            />
          </button>
        </div>
      </div>
      <div
        v-if="activeTemplate && isExpanded"
        class="space-y-5 rounded-md rounded-t-none border border-t-0 border-gray-200 p-4"
      >
        <lf-input
          :value="step.name"
          :name="`step-base-${step.id}--name`"
          :placeholder="t('CHECKOUT.STEP_NAME')"
          data-cy="step-name-input"
          @key-released="activeTemplate.content.steps[stepIndex].name = $event"
        />
        <lf-textarea
          :value="step.description ?? ''"
          :name="`step-base-${step.id}--description`"
          :placeholder="t('CHECKOUT.STEP_DESCRIPTION')"
          data-cy="step-description-input"
          rows="3"
          @key-released="
            activeTemplate.content.steps[stepIndex].description = $event
          "
        />
        <lf-dropdown
          :value="step.type"
          :options="typeOptions"
          :placeholder="t('COMMON.TYPE')"
          full-width
          :name="`step-base-${step.id}--type`"
          data-cy="step-type-dropdown"
          @change="handleStepTypeChange"
        />
        <component :is="stepDetailsComponent" v-if="!!stepDetailsComponent" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRaw } from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { storeToRefs } from "pinia";

import { useCheckoutOptionsQuery } from "@/hooks/queries/options";
import { useCheckoutStore } from "@/stores/checkout";

import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import IconTrashCanOutline from "@/components/icons/IconTrashCanOutline.vue";
import IconChevronDown from "@/components/icons/deals/IconChevronDown.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfTextarea from "@/components/ui/inputs/LfTextarea.vue";
import ConsentsStepDetails from "@/views/checkout/components/ConsentsStepDetails.vue";
import ContractsStepDetails from "@/views/checkout/components/ContractsStepDetails.vue";
import CustomStepDetails from "@/views/checkout/components/CustomStepDetails.vue";
import DataStepDetails from "@/views/checkout/components/DataStepDetails.vue";
import ServiceStepDetails from "@/views/checkout/components/ServiceStepDetails.vue";

import type { CheckoutStep, CheckoutStepType } from "@/models/checkout";
import type { Component } from "vue";

const STEP_TO_STEP_DETAILS = {
  documents: null,
  consents: ConsentsStepDetails,
  contracts: ContractsStepDetails,
  data: DataStepDetails,
  service: ServiceStepDetails,
  custom: CustomStepDetails,
} satisfies Record<CheckoutStepType, Component | null>;

type BaseStepFields = Pick<CheckoutStep, "id" | "name" | "description">;

const STEP_FACTORY: Record<
  CheckoutStepType,
  (base: BaseStepFields) => CheckoutStep
> = {
  documents: (base) => ({ ...base, type: "documents" }),
  consents: (base) => ({ ...base, type: "consents", templates: [] }),
  contracts: (base) => ({ ...base, type: "contracts", templates: [] }),
  data: (base) => ({ ...base, type: "data", items: [] }),
  service: (base) => ({ ...base, type: "service", service_id: "" }),
  custom: (base) => ({
    ...base,
    type: "custom",
    custom_title: null,
    custom_description: null,
    custom_button_text: null,
    custom_button_url: null,
  }),
};

const { step, stepNumber } = defineProps<{
  step: CheckoutStep;
  stepNumber: number;
}>();

const { t } = useI18n();
const checkoutStore = useCheckoutStore();
const { data: options } = useCheckoutOptionsQuery();
const { activeTemplate, activeTemplateSelectedStepId } =
  storeToRefs(checkoutStore);

const typeOptions = computed(() => {
  const filteredOptions = toRaw(options.value?.definition.step_types);

  if (!filteredOptions) {
    return {};
  }

  return filteredOptions;
});

const isExpanded = computed(
  () => activeTemplateSelectedStepId.value === step.id
);

const stepDetailsComponent = computed(() => {
  if (!step.type) {
    return null;
  }
  return STEP_TO_STEP_DETAILS[step.type];
});

const stepIndex = computed(() => {
  if (!activeTemplate.value) {
    return -1;
  }

  return activeTemplate.value.content.steps.findIndex((s) => s.id === step.id);
});

const isLastStep = computed(() => {
  if (!activeTemplate.value) {
    return true;
  }

  return stepIndex.value === activeTemplate.value?.content.steps.length - 1;
});

const handleStepTypeChange = (newStepType: string | number) => {
  if (!activeTemplate.value?.content.steps[stepIndex.value]) {
    return;
  }

  const currentStep = activeTemplate.value.content.steps[stepIndex.value];
  const stepType = newStepType as CheckoutStepType;

  const baseStep: BaseStepFields = {
    id: currentStep.id,
    name: currentStep.name,
    description: currentStep.description,
  };

  const stepFactory = STEP_FACTORY[stepType];
  if (!stepFactory) {
    Sentry.captureException(
      new Error(`Invalid checkout step type: ${stepType}`),
      {
        extra: {
          stepType,
          currentStepId: currentStep.id,
          availableTypes: Object.keys(STEP_FACTORY),
        },
      }
    );
    return;
  }

  const newStep = stepFactory(baseStep);
  activeTemplate.value.content.steps[stepIndex.value] = newStep;
};

const expand = () => {
  if (isExpanded.value) {
    activeTemplateSelectedStepId.value = null;
    return;
  }

  activeTemplateSelectedStepId.value = step.id;
};

const removeStep = () => {
  if (!activeTemplate.value) {
    return;
  }

  activeTemplate.value.content.steps =
    activeTemplate.value.content.steps.filter((s) => s.id !== step.id);
};
</script>
